import React, { useState, useEffect, useRef } from 'react';
import {
  Text,
  Button,
  Input,
  Avatar,
  Spinner,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  Send24Regular,
  Add24Regular,
  Search24Regular,
  Attach24Regular,
} from '@fluentui/react-icons';
import { usePortalAuth } from '../contexts/PortalAuthContext';
import { Conversation, PortalMessage } from '../types';
import { formatBusinessTime, formatBusinessDate } from '../../lib/businessTime';

const useStyles = makeStyles({
  container: {
    display: 'grid',
    gridTemplateColumns: '320px 1fr',
    height: 'calc(100vh - 180px)',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('8px'),
    boxShadow: tokens.shadow4,
    ...shorthands.overflow('hidden'),
    '@media (max-width: 900px)': {
      gridTemplateColumns: '1fr',
    },
  },
  sidebar: {
    ...shorthands.borderRight('1px', 'solid', tokens.colorNeutralStroke2),
    display: 'flex',
    flexDirection: 'column',
  },
  sidebarHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.padding('16px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  sidebarTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  searchBox: {
    ...shorthands.padding('12px', '16px'),
  },
  conversationList: {
    flex: 1,
    overflowY: 'auto',
  },
  conversationItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    ...shorthands.padding('12px', '16px'),
    cursor: 'pointer',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  conversationItemActive: {
    backgroundColor: tokens.colorNeutralBackground1Selected,
  },
  conversationContent: {
    flex: 1,
    minWidth: 0,
  },
  conversationSubject: {
    fontSize: '14px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    marginBottom: '4px',
  },
  conversationPreview: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  conversationTime: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    alignSelf: 'flex-start',
  },
  unreadBadge: {
    width: '8px',
    height: '8px',
    ...shorthands.borderRadius('50%'),
    backgroundColor: tokens.colorBrandForeground1,
    alignSelf: 'flex-start',
    marginTop: '6px',
  },
  chatArea: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  chatHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    ...shorthands.padding('16px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  chatHeaderInfo: {
    flex: 1,
  },
  chatHeaderTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  chatHeaderSubtitle: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
  },
  messagesArea: {
    flex: 1,
    overflowY: 'auto',
    ...shorthands.padding('16px'),
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  messageGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  message: {
    display: 'flex',
    gap: '12px',
    maxWidth: '70%',
  },
  messageSent: {
    alignSelf: 'flex-end',
    flexDirection: 'row-reverse',
  },
  messageReceived: {
    alignSelf: 'flex-start',
  },
  messageContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  messageBubble: {
    ...shorthands.padding('12px', '16px'),
    ...shorthands.borderRadius('12px'),
    fontSize: '14px',
    lineHeight: '1.5',
  },
  messageBubbleSent: {
    backgroundColor: tokens.colorBrandBackground,
    color: '#ffffff',
    borderBottomRightRadius: '4px',
  },
  messageBubbleReceived: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground1,
    borderBottomLeftRadius: '4px',
  },
  messageTime: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
  },
  messageTimeSent: {
    textAlign: 'right' as const,
  },
  inputArea: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    ...shorthands.padding('16px'),
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
  },
  inputField: {
    flex: 1,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: tokens.colorNeutralForeground3,
    ...shorthands.padding('32px'),
    textAlign: 'center' as const,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    ...shorthands.padding('48px'),
  },
});

// Mock data
const mockConversations: Conversation[] = [
  {
    id: 'conv-1',
    subject: 'Order #1234 Support',
    lastMessage: {
      id: 'msg-1',
      conversationId: 'conv-1',
      sender: { id: 'staff-1', name: 'TWM Support', type: 'staff' },
      text: 'Your pickup has been scheduled for December 15th between 8-10am.',
      timestamp: '2025-12-08T14:30:00Z',
      read: false,
    },
    unreadCount: 1,
    createdAt: '2025-12-07T10:00:00Z',
    updatedAt: '2025-12-08T14:30:00Z',
  },
  {
    id: 'conv-2',
    subject: 'Billing Question',
    lastMessage: {
      id: 'msg-5',
      conversationId: 'conv-2',
      sender: { id: 'staff-1', name: 'TWM Billing', type: 'staff' },
      text: 'Your invoice has been updated. Let me know if you have any questions.',
      timestamp: '2025-12-05T16:00:00Z',
      read: true,
    },
    unreadCount: 0,
    createdAt: '2025-12-03T09:00:00Z',
    updatedAt: '2025-12-05T16:00:00Z',
  },
  {
    id: 'conv-3',
    subject: 'General Inquiry',
    lastMessage: {
      id: 'msg-10',
      conversationId: 'conv-3',
      sender: { id: 'customer-1', name: 'You', type: 'customer' },
      text: 'Thank you for the information!',
      timestamp: '2025-11-28T11:00:00Z',
      read: true,
    },
    unreadCount: 0,
    createdAt: '2025-11-25T14:00:00Z',
    updatedAt: '2025-11-28T11:00:00Z',
  },
];

const mockMessages: Record<string, PortalMessage[]> = {
  'conv-1': [
    {
      id: 'msg-1-1',
      conversationId: 'conv-1',
      sender: { id: 'customer-1', name: 'You', type: 'customer' },
      text: 'Hi, I need to schedule a pickup for my dumpster. Order #1234.',
      timestamp: '2025-12-07T10:00:00Z',
      read: true,
    },
    {
      id: 'msg-1-2',
      conversationId: 'conv-1',
      sender: { id: 'staff-1', name: 'TWM Support', type: 'staff' },
      text: 'Hello! I can help you with that. When would you like to schedule the pickup?',
      timestamp: '2025-12-07T10:15:00Z',
      read: true,
    },
    {
      id: 'msg-1-3',
      conversationId: 'conv-1',
      sender: { id: 'customer-1', name: 'You', type: 'customer' },
      text: 'December 15th would be ideal. Morning if possible.',
      timestamp: '2025-12-07T10:20:00Z',
      read: true,
    },
    {
      id: 'msg-1-4',
      conversationId: 'conv-1',
      sender: { id: 'staff-1', name: 'TWM Support', type: 'staff' },
      text: 'Your pickup has been scheduled for December 15th between 8-10am. Our driver José will contact you when he\'s on the way.',
      timestamp: '2025-12-08T14:30:00Z',
      read: false,
    },
  ],
  'conv-2': [
    {
      id: 'msg-2-1',
      conversationId: 'conv-2',
      sender: { id: 'customer-1', name: 'You', type: 'customer' },
      text: 'I have a question about my recent invoice. The amount seems different from what was quoted.',
      timestamp: '2025-12-03T09:00:00Z',
      read: true,
    },
    {
      id: 'msg-2-2',
      conversationId: 'conv-2',
      sender: { id: 'staff-1', name: 'TWM Billing', type: 'staff' },
      text: 'Thank you for reaching out. Let me review your account and get back to you.',
      timestamp: '2025-12-03T09:30:00Z',
      read: true,
    },
    {
      id: 'msg-2-3',
      conversationId: 'conv-2',
      sender: { id: 'staff-1', name: 'TWM Billing', type: 'staff' },
      text: 'I found the discrepancy - there was an additional disposal fee that wasn\'t included in the original quote. I\'ve updated the invoice to reflect the correct amount.',
      timestamp: '2025-12-05T16:00:00Z',
      read: true,
    },
  ],
};

const Messages: React.FC = () => {
  const styles = useStyles();
  const { user } = usePortalAuth();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<PortalMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const loadConversations = async () => {
      setLoading(true);
      try {
        await new Promise(resolve => setTimeout(resolve, 500));
        setConversations(mockConversations);
        if (mockConversations.length > 0) {
          setSelectedConversation(mockConversations[0]);
        }
      } finally {
        setLoading(false);
      }
    };
    loadConversations();
  }, []);

  useEffect(() => {
    if (selectedConversation) {
      setMessages(mockMessages[selectedConversation.id] || []);
    }
  }, [selectedConversation]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = () => {
    if (!inputValue.trim() || !selectedConversation) return;

    const newMessage: PortalMessage = {
      id: `msg-${Date.now()}`,
      conversationId: selectedConversation.id,
      sender: {
        id: user?.id || 'customer-1',
        name: 'You',
        type: 'customer',
      },
      text: inputValue,
      timestamp: new Date().toISOString(),
      read: true,
    };

    setMessages(prev => [...prev, newMessage]);
    setInputValue('');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return formatBusinessTime(dateString);
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    } else {
      return formatBusinessDate(dateString);
    }
  };

  const formatMessageTime = (dateString: string) => formatBusinessTime(dateString);

  // Filter conversations
  const filteredConversations = conversations.filter(conv =>
    searchQuery === '' ||
    conv.subject.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spinner size="large" label="Loading messages..." />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Sidebar */}
      <div className={styles.sidebar}>
        <div className={styles.sidebarHeader}>
          <Text className={styles.sidebarTitle}>Messages</Text>
          <Button appearance="subtle" icon={<Add24Regular />} title="New conversation" />
        </div>

        <div className={styles.searchBox}>
          <Input
            contentBefore={<Search24Regular />}
            placeholder="Search messages..."
            value={searchQuery}
            onChange={(e, data) => setSearchQuery(data.value)}
            style={{ width: '100%' }}
          />
        </div>

        <div className={styles.conversationList}>
          {filteredConversations.map((conv) => (
            <div
              key={conv.id}
              role="button"
              tabIndex={0}
              aria-pressed={selectedConversation?.id === conv.id}
              aria-label={`Conversation: ${conv.subject}`}
              className={`${styles.conversationItem} ${
                selectedConversation?.id === conv.id ? styles.conversationItemActive : ''
              }`}
              onClick={() => setSelectedConversation(conv)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelectedConversation(conv);
                }
              }}
            >
              <Avatar name="TWM" size={40} color="brand" />
              <div className={styles.conversationContent}>
                <Text className={styles.conversationSubject}>{conv.subject}</Text>
                <Text className={styles.conversationPreview}>
                  {conv.lastMessage?.text}
                </Text>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                <Text className={styles.conversationTime}>
                  {formatTime(conv.updatedAt)}
                </Text>
                {conv.unreadCount > 0 && <div className={styles.unreadBadge} />}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Chat Area */}
      <div className={styles.chatArea}>
        {selectedConversation ? (
          <>
            <div className={styles.chatHeader}>
              <Avatar name="TWM" size={40} color="brand" />
              <div className={styles.chatHeaderInfo}>
                <Text className={styles.chatHeaderTitle}>{selectedConversation.subject}</Text>
                <Text className={styles.chatHeaderSubtitle}>TWM Support Team</Text>
              </div>
            </div>

            <div className={styles.messagesArea}>
              {messages.map((message) => {
                const isSent = message.sender.type === 'customer';
                return (
                  <div
                    key={message.id}
                    className={`${styles.message} ${isSent ? styles.messageSent : styles.messageReceived}`}
                  >
                    {!isSent && <Avatar name={message.sender.name} size={32} color="colorful" />}
                    <div className={styles.messageContent}>
                      <div
                        className={`${styles.messageBubble} ${
                          isSent ? styles.messageBubbleSent : styles.messageBubbleReceived
                        }`}
                      >
                        {message.text}
                      </div>
                      <Text className={`${styles.messageTime} ${isSent ? styles.messageTimeSent : ''}`}>
                        {formatMessageTime(message.timestamp)}
                      </Text>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            <div className={styles.inputArea}>
              <Button appearance="subtle" icon={<Attach24Regular />} />
              <Input
                className={styles.inputField}
                placeholder="Type a message..."
                value={inputValue}
                onChange={(e, data) => setInputValue(data.value)}
                onKeyPress={handleKeyPress}
              />
              <Button
                appearance="primary"
                icon={<Send24Regular />}
                onClick={handleSendMessage}
                disabled={!inputValue.trim()}
              />
            </div>
          </>
        ) : (
          <div className={styles.emptyState}>
            <Text style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Select a conversation
            </Text>
            <Text>Choose a conversation from the list to view messages</Text>
          </div>
        )}
      </div>
    </div>
  );
};

export default Messages;
